import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronRight, Heart, Ruler, Truck, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, eur, imgW } from "@/lib/api";
import { SizeAlert } from "@/components/SizeAlert";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { useSeo } from "@/lib/seo";

const SIZE_TABLE = [
  ["36", "3.5", "4", "22.5"], ["37", "4", "4.5", "23"], ["38", "5", "5.5", "24"], ["39", "6", "6.5", "24.5"], ["40", "6.5", "7", "25"],
  ["41", "7", "8", "26"], ["42", "7.5", "8.5", "26.5"], ["42.5", "8", "9", "27"], ["43", "8.5", "9.5", "27.5"], ["44", "9", "10", "28"],
  ["44.5", "9.5", "10.5", "28.5"], ["45", "10", "11", "29"], ["46", "11", "12", "30"], ["47", "12", "13", "31"],
];

function ZoomImage({ src, alt, onOpen, testid }) {
  const [origin, setOrigin] = useState("50% 50%");
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onOpen} data-testid={testid}
      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="relative block aspect-square w-full cursor-zoom-in overflow-hidden bg-white">
      <img src={imgW(src, 1400)} alt={alt} className="h-full w-full object-contain transition-transform duration-300 ease-out"
        style={{ transformOrigin: origin, transform: hover ? "scale(1.9)" : "scale(1)" }} />
    </button>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { t, lang, addToCart, toggleWish, isWished } = useStore();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [size, setSize] = useState(null);
  const [sizeErr, setSizeErr] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [guide, setGuide] = useState(false);
  const [mobileImg, setMobileImg] = useState(0);

  useEffect(() => {
    setData(null); setSize(null); setErr(false); setMobileImg(0);
    window.scrollTo({ top: 0 });
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  const p = data?.product;
  const jsonLd = useMemo(() => p && ({
    "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.sku, image: p.images, description: p.description,
    brand: { "@type": "Brand", name: p.brand }, color: p.colorway,
    offers: { "@type": "Offer", priceCurrency: "EUR", price: p.final_price, url: window.location.href,
      availability: p.total_stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
  }), [p]);
  useSeo({ title: p ? `${p.name}` : "Product", description: p ? `${p.name} by ${p.brand}. ${eur(p.final_price)}. EU sizes, shipping across Europe.` : undefined, image: p?.images?.[0], jsonLd });

  if (err) return <main className="container-x py-32"><h1 className="font-display text-5xl font-black uppercase">Not found</h1><Link to="/shop" className="btn-ink mt-8">{t("shopNow")}</Link></main>;
  if (!p) return <main className="container-x grid gap-10 py-10 lg:grid-cols-12"><div className="skeleton-shimmer aspect-square lg:col-span-7" /><div className="space-y-4 lg:col-span-5"><div className="skeleton-shimmer h-6 w-1/3" /><div className="skeleton-shimmer h-12" /><div className="skeleton-shimmer h-64" /></div></main>;

  const stock = size ? p.sizes.find((s) => s.size === size)?.stock ?? 0 : null;
  const wished = isWished(p.id);
  const add = () => { if (!size) { setSizeErr(true); return; } addToCart(p, size); };
  const stockLabel = p.total_stock === 0 ? t("soldOut") : stock == null ? t("inStock") : stock <= 2 ? t("onlyLeft", { n: stock }) : t("inStock");

  return (
    <main className="pb-28 lg:pb-0" data-testid="product-page">
      <nav className="container-x flex items-center gap-2 py-5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-ink">Home</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/collections/${p.brand_slug}`} className="hover:text-ink">{p.brand}</Link><ChevronRight className="h-3 w-3" />
        <Link to={`/collections/${p.brand_slug}?model=${encodeURIComponent(p.model)}`} className="hover:text-ink">{p.model}</Link>
      </nav>
      <div className="container-x grid gap-10 lg:grid-cols-12 lg:gap-14">
        {/* gallery */}
        <div className="lg:col-span-7">
          <div className="hidden grid-cols-2 gap-2 lg:grid" data-testid="product-gallery">
            {p.images.map((src, i) => (
              <motion.div key={src + i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className={i === 0 ? "col-span-2" : ""}>
                <ZoomImage src={src} alt={`${p.name} view ${i + 1}`} onOpen={() => setZoom(i)} testid={`gallery-image-${i}`} />
              </motion.div>
            ))}
          </div>
          <div className="lg:hidden">
            <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory overflow-x-auto" onScroll={(e) => setMobileImg(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))} data-testid="mobile-gallery">
              {p.images.map((src, i) => (
                <button key={src + i} onClick={() => setZoom(i)} className="aspect-square w-full shrink-0 snap-center bg-white">
                  <img src={imgW(src, 900)} alt={`${p.name} view ${i + 1}`} className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
            <div className="mt-3 flex justify-center gap-1.5">{p.images.map((_, i) => <span key={i} className={`h-0.5 transition-[width,background-color] ${mobileImg === i ? "w-8 bg-ink" : "w-4 bg-concrete"}`} />)}</div>
          </div>
        </div>

        {/* info */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="eyebrow text-ink/50" data-testid="product-brand">{p.brand} · {p.model}</p>
            <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl" data-testid="product-title">{p.name}</h1>
            <div className="mt-5 flex items-baseline gap-3 font-mono" data-testid="product-detail-price">
              <span className={`text-2xl ${p.on_sale ? "text-ink" : ""}`}>{eur(p.final_price, lang)}</span>
              {p.on_sale && <><span className="text-ink/40 line-through">{eur(p.price, lang)}</span><span className="bg-lavender px-2 py-0.5 text-[10px] font-bold uppercase text-ink">-{Math.round((1 - p.final_price / p.price) * 100)}%</span></>}
            </div>
            <p className="mt-2 font-mono text-[11px] text-ink/40">SKU {p.sku} · {t("vatIncl")}</p>
            {p.is_demo && <p className="mt-4 border-l-2 border-silver pl-3 text-xs text-ink/60" data-testid="demo-notice">{t("demoNotice")}</p>}

            {data.colorways.length > 1 && (
              <div className="mt-8">
                <p className="eyebrow mb-3">{t("colorway")}: <span className="text-ink/60">{p.colorway}</span></p>
                <div className="flex flex-wrap gap-2" data-testid="colorway-selector">
                  {data.colorways.map((c) => (
                    <Link key={c.slug} to={`/product/${c.slug}`} title={c.colorway} data-testid={`colorway-${c.slug}`}
                      className={`h-16 w-16 overflow-hidden border-2 bg-white transition-[border-color,transform] hover:-translate-y-0.5 ${c.slug === p.slug ? "border-ink" : "border-transparent"}`}>
                      <img src={imgW(c.image, 200)} alt={c.colorway} className="h-full w-full object-contain" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between">
                <p className={`eyebrow ${sizeErr && !size ? "text-ink" : ""}`}>{t("selectSize")} (EU)</p>
                <button onClick={() => setGuide(true)} className="link-u flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider" data-testid="size-guide-btn"><Ruler className="h-3.5 w-3.5" />{t("sizeGuide")}</button>
              </div>
              <div className={`grid grid-cols-4 gap-1.5 sm:grid-cols-5 ${sizeErr && !size ? "animate-pulse" : ""}`} data-testid="size-selector">
                {p.sizes.map((s) => (
                  <button key={s.size} disabled={s.stock <= 0} onClick={() => { setSize(s.size); setSizeErr(false); }} data-testid={`size-option-${s.size}`}
                    className={`relative border py-3.5 font-mono text-sm transition-colors disabled:cursor-not-allowed disabled:bg-concrete/50 disabled:text-ink/25 ${size === s.size ? "border-ink bg-ink text-sun" : "border-concrete hover:border-ink"}`}>
                    {s.size}
                    {s.stock <= 0 && <span className="absolute inset-0 m-auto h-px w-3/4 rotate-[-20deg] bg-ink/20" />}
                  </button>
                ))}
              </div>
              <p className="mt-3 flex items-center gap-2 text-sm" data-testid="stock-info">
                <span className={`h-2 w-2 rounded-full ${p.total_stock === 0 ? "bg-ink/30" : stock != null && stock <= 2 ? "bg-lavender" : "bg-ink"}`} />{stockLabel}
              </p>
              <SizeAlert product={p} />
            </div>

            <div className="mt-8 hidden gap-2 lg:flex">
              <button className="btn-ink flex-1 py-5" onClick={add} disabled={p.total_stock === 0} data-testid="add-to-bag-btn">{p.total_stock === 0 ? t("soldOut") : t("addToBag")}</button>
              <button onClick={() => toggleWish(p)} className="grid w-16 place-items-center border border-concrete transition-colors hover:border-ink" aria-label={t("wishlist")} data-testid="pdp-wishlist-btn">
                <Heart className={`h-5 w-5 ${wished ? "fill-lavender text-ink" : ""}`} strokeWidth={1.6} />
              </button>
            </div>
            <p className="mt-5 flex items-center gap-2 text-xs text-ink/60"><Truck className="h-4 w-4" /> Portugal 2–3 days · Europe 3–6 days</p>

            <Accordion type="single" collapsible defaultValue="desc" className="mt-8 border-t border-concrete">
              <AccordionItem value="desc"><AccordionTrigger className="eyebrow">{t("description")}</AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm leading-relaxed text-ink/70">
                  <p>{p.description}</p>
                  <ul className="space-y-1 font-mono text-xs"><li>{t("style")}: {p.sku}</li><li>{t("colorway")}: {p.colorway}</li><li>{t("gender")}: {p.gender}</li></ul>
                </AccordionContent></AccordionItem>
              <AccordionItem value="ship"><AccordionTrigger className="eyebrow">{t("shippingReturns")}</AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm leading-relaxed text-ink/70"><p>{t("shippingText")}</p><p>{t("returnsText")}</p></AccordionContent></AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {data.related.length > 0 && (
        <section className="container-x py-24 sm:py-32" data-testid="related-products">
          <h2 className="mb-10 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">{t("related")}</h2>
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4">{data.related.slice(0, 4).map((r, i) => <ProductCard key={r.id} p={r} index={i} />)}</div>
        </section>
      )}

      {/* sticky mobile add to cart */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-concrete bg-sun/95 p-3 backdrop-blur-xl lg:hidden" data-testid="mobile-sticky-atc">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-ink/60">{size ? `EU ${size}` : t("selectSize")}</p>
            <p className="font-mono text-sm font-semibold">{eur(p.final_price, lang)}</p>
          </div>
          <button onClick={() => toggleWish(p)} className="grid h-12 w-12 place-items-center border border-concrete" aria-label={t("wishlist")} data-testid="mobile-wishlist-btn">
            <Heart className={`h-5 w-5 ${wished ? "fill-lavender text-ink" : ""}`} strokeWidth={1.6} />
          </button>
          <button className="btn-ink h-12 px-6" onClick={() => (size ? add() : (setSizeErr(true), document.querySelector('[data-testid="size-selector"]')?.scrollIntoView({ behavior: "smooth", block: "center" })))}
            disabled={p.total_stock === 0} data-testid="mobile-add-to-bag-btn">{t("addToBag")}</button>
        </div>
      </div>

      <Dialog open={zoom != null} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="h-[100svh] max-w-none rounded-none border-0 bg-white p-0 sm:rounded-none [&>button]:hidden" data-testid="zoom-dialog">
          <DialogTitle className="sr-only">{p.name}</DialogTitle>
          <button onClick={() => setZoom(null)} className="absolute right-5 top-5 z-10 grid h-11 w-11 place-items-center bg-sun" aria-label="Close" data-testid="zoom-close-btn"><X className="h-5 w-5" /></button>
          <AnimatePresence mode="wait">
            {zoom != null && <motion.img key={zoom} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} src={imgW(p.images[zoom], 2000)} alt={p.name} className="h-full w-full object-contain" />}
          </AnimatePresence>
          <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
            {p.images.map((src, i) => (
              <button key={i} onClick={() => setZoom(i)} className={`h-14 w-14 overflow-hidden border-2 bg-white ${zoom === i ? "border-ink" : "border-transparent"}`} data-testid={`zoom-thumb-${i}`}>
                <img src={imgW(src, 150)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent className="max-w-lg rounded-none" data-testid="size-guide-dialog">
          <DialogTitle className="font-display text-2xl font-black uppercase">{t("sizeGuide")}</DialogTitle>
          <table className="mt-2 w-full font-mono text-sm">
            <thead><tr className="border-b border-ink text-left text-[11px] uppercase tracking-widest"><th className="py-2">EU</th><th>UK</th><th>US M</th><th>CM</th></tr></thead>
            <tbody>{SIZE_TABLE.map((r) => (
              <tr key={r[0]} className={`border-b border-concrete ${size === r[0] ? "bg-concrete" : ""}`}>{r.map((c, i) => <td key={i} className="py-2">{c}{i === 0 && size === r[0] && <Check className="ml-1 inline h-3 w-3" />}</td>)}</tr>
            ))}</tbody>
          </table>
          <p className="text-xs text-ink/50">Approximate conversion. Fit varies by model.</p>
        </DialogContent>
      </Dialog>
    </main>
  );
}
